import React from 'react';

const cx = (...a) => a.filter(Boolean).join(' ');

const styleVariant = (variant) =>
  variant === 'dashed' ? 'border-dashed' : variant === 'dotted' ? 'border-dotted' : 'border-solid';

const thicknessToClass = (thickness) => {
  if (typeof thickness === 'number') {
    const clamped = Math.max(0, Math.min(8, thickness));
    return { className: null, style: { borderTopWidth: clamped } };
  }
  if (typeof thickness === 'string') {
    return { className: null, style: { borderTopWidth: thickness } };
  }
  return { className: null, style: {} };
};

export default function Divider({
  as: As = 'hr',
  orientation = 'horizontal',
  variant = 'solid',
  label,
  labelAlign = 'center',
  inset = 0,
  thickness = 1,
  spaced = true,
  className = '',
  lineClassName = '',
  style,
}) {
  const variantClass = styleVariant(variant);
  const { className: thicknessClass, style: thicknessStyle } = thicknessToClass(thickness);

  if (orientation === 'vertical') {
    if (label) {
      return (
        <div
          role="separator"
          aria-orientation="vertical"
          className={cx(
            'flex h-full flex-col items-center',
            spaced && 'mx-2',
            className
          )}
          style={style}
        >
          <div
            className={cx('flex-1 border-l border-gray-200', variantClass)}
            style={{ ...thicknessStyle, marginTop: inset, marginBottom: inset }}
          />
          <div className="py-1">{label}</div>
          <div
            className={cx('flex-1 border-l border-gray-200', variantClass)}
            style={{ ...thicknessStyle, marginTop: inset, marginBottom: inset }}
          />
        </div>
      );
    }

    return (
      <div
        role="separator"
        aria-orientation="vertical"
        className={cx(spaced && 'mx-2', className)}
        style={{ ...style }}
      >
        <div
          className={cx('h-full border-l border-gray-200', variantClass, lineClassName, thicknessClass)}
          style={{ ...thicknessStyle, marginTop: inset, marginBottom: inset }}
        />
      </div>
    );
  }

  if (label) {
    const alignMap = {
      start: 'justify-start',
      center: 'justify-center',
      end: 'justify-end',
    };
    return (
      <div
        role="separator"
        className={cx(
          'flex w-full items-center',
          spaced && 'my-4',
          className
        )}
        style={style}
      >
        <div
          className={cx('flex-1 border-t border-gray-200', variantClass, lineClassName, thicknessClass)}
          style={{ ...thicknessStyle, marginLeft: inset, marginRight: inset }}
        />
        <div className={cx('px-3', alignMap[labelAlign] || '')}>{label}</div>
        <div
          className={cx('flex-1 border-t border-gray-200', variantClass, lineClassName, thicknessClass)}
          style={{ ...thicknessStyle, marginLeft: inset, marginRight: inset }}
        />
      </div>
    );
  }

  return (
    <As
      role="separator"
      className={cx(
        'border-0 border-t border-gray-200',
        variantClass,
        spaced && 'my-4',
        lineClassName,
        className
      )}
      style={{ ...style, ...thicknessStyle, marginLeft: inset, marginRight: inset }}
      aria-orientation="horizontal"
    />
  );
}